/**
 * Edit a single chat.
 *
 * What lives here:
 *   - Title (rare; usually inherited from Telegram, but we let admins override)
 *   - Guarantor @handle  (with explicit "auto-detected" badge and reset button)
 *   - Custom template assignment (overrides global mailing template)
 *   - is_enabled toggle (a quick pause without removing the chat)
 *   - Destructive: delete
 *
 * Doesn't expose peer_id / chat_ref / sort_order — those are read-only.
 */

import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarDays,
  Check,
  Clock,
  Eye,
  EyeOff,
  Lock,
  Power,
  ShieldCheck,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button, Input, Sheet } from "@/design";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";
import {
  useDeleteChat,
  useUpdateChat,
  type Chat,
} from "@/api/chats";
import { useTemplates } from "@/api/templates";

interface ChatEditSheetProps {
  open: boolean;
  onClose: () => void;
  chat: Chat | null;
}

export function ChatEditSheet({ open, onClose, chat }: ChatEditSheetProps) {
  const [title, setTitle] = useState("");
  const [guarantor, setGuarantor] = useState("");
  const [autoDetected, setAutoDetected] = useState(false);
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [customScheduleEnabled, setCustomScheduleEnabled] = useState(false);
  const [customPostsPerDay, setCustomPostsPerDay] = useState(3);
  const [customIntervalMinutes, setCustomIntervalMinutes] = useState(180);

  const { data: templates } = useTemplates();
  const updateMut = useUpdateChat(chat?.id ?? 0);
  const deleteMut = useDeleteChat();

  // Seed from chat each time it opens
  useEffect(() => {
    if (!chat || !open) return;
    setTitle(chat.title);
    setGuarantor(chat.guarantor ? `@${chat.guarantor}` : "");
    setAutoDetected(chat.guarantor_auto_detected);
    setTemplateId(chat.custom_template_id);
    setEnabled(chat.is_enabled);
    setCustomScheduleEnabled(chat.custom_schedule_enabled);
    setCustomPostsPerDay(chat.custom_posts_per_day);
    setCustomIntervalMinutes(chat.custom_interval_minutes);
  }, [chat, open]);

  if (!chat) return null;

  const dirty =
    title.trim() !== chat.title ||
    normalizeGuarantor(guarantor) !== (chat.guarantor ?? "") ||
    templateId !== chat.custom_template_id ||
    enabled !== chat.is_enabled ||
    customScheduleEnabled !== chat.custom_schedule_enabled ||
    customPostsPerDay !== chat.custom_posts_per_day ||
    customIntervalMinutes !== chat.custom_interval_minutes;

  const handleSave = async () => {
    haptic.impact("medium");
    try {
      await updateMut.mutateAsync({
        title: title.trim() || undefined,
        guarantor: normalizeGuarantor(guarantor) || null,
        custom_template_id: templateId ?? 0, // 0 = clear on backend
        is_enabled: enabled,
        custom_schedule_enabled: customScheduleEnabled,
        custom_posts_per_day: customPostsPerDay,
        custom_interval_minutes: customIntervalMinutes,
      });
      haptic.notify("success");
      toast.success("Сохранено");
      onClose();
    } catch (err) {
      haptic.notify("error");
      toast.error((err as Error)?.message || "Не удалось сохранить");
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Удалить чат «${chat.title}»?`)) return;
    haptic.impact("heavy");
    try {
      await deleteMut.mutateAsync(chat.id);
      haptic.notify("success");
      toast.success("Чат удалён");
      onClose();
    } catch {
      haptic.notify("error");
      toast.error("Не удалось удалить");
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      size="full"
      title="Чат"
      description={chat.chat_ref}
    >
      <div className="space-y-5">
        {/* Identity card */}
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 space-y-2">
          <div className="flex items-center gap-2.5">
            <div className="size-10 rounded-xl bg-accent-soft text-accent grid place-items-center shrink-0">
              <PeerIcon type={chat.peer_type} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-medium truncate">{chat.title}</div>
              <div className="text-[11px] text-text-muted truncate flex items-center gap-1.5">
                <span className="truncate">{chat.chat_ref}</span>
                {chat.topic_message_id != null && (
                  <span className="text-text-faint">· топик #{chat.topic_message_id}</span>
                )}
              </div>
            </div>
          </div>

          {chat.consecutive_failures > 0 && (
            <div className="flex items-center gap-2 text-[11px] text-warning bg-warning/[0.06] border border-warning/15 rounded-xl px-3 py-2">
              <Lock className="size-3" />
              {chat.consecutive_failures} подряд неудачных публикаций
            </div>
          )}
        </div>

        {/* Enabled toggle */}
        <button
          onClick={() => {
            setEnabled((v) => !v);
            haptic.select();
          }}
          className={cn(
            "w-full glass rounded-2xl p-3.5 flex items-center gap-3",
            "hover:bg-white/[0.05] transition-colors text-left",
          )}
        >
          <div
            className={cn(
              "size-10 rounded-xl grid place-items-center shrink-0",
              enabled ? "bg-success/15 text-success" : "bg-white/[0.04] text-text-dim",
            )}
          >
            {enabled ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">
              {enabled ? "Чат активен" : "Чат на паузе"}
            </div>
            <div className="text-xs text-text-muted">
              {enabled
                ? "Участвует в рассылках"
                : "Не получает публикации, остаётся в списке"}
            </div>
          </div>
          <div
            className={cn(
              "relative w-10 h-6 rounded-full transition-colors shrink-0",
              enabled ? "bg-success" : "bg-white/[0.08]",
            )}
          >
            <motion.div
              animate={{ x: enabled ? 16 : 0 }}
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
              className="absolute top-0.5 left-0.5 size-5 rounded-full bg-white"
            />
          </div>
        </button>

        {/* Title */}
        <div>
          <Input
            label="Название (как мы его видим)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={chat.title}
            maxLength={256}
          />
        </div>

        {/* Guarantor */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between px-1">
            <label className="text-xs font-medium text-text-muted">Гарант</label>
            {autoDetected && guarantor && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium text-accent">
                <Sparkles className="size-2.5" />
                автоопределён
              </span>
            )}
          </div>
          <Input
            value={guarantor}
            onChange={(e) => {
              const v = e.target.value;
              setGuarantor(v.startsWith("@") || v === "" ? v : `@${v.replace(/@/g, "")}`);
              setAutoDetected(false);
            }}
            placeholder="@username"
            leftIcon={<ShieldCheck />}
            hint="Будет проверен по нику в рассылке"
          />
        </div>

        {/* Template override */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between px-1">
            <label className="text-xs font-medium text-text-muted">
              Свой шаблон для этого чата
            </label>
            {templateId !== null && (
              <button
                onClick={() => {
                  setTemplateId(null);
                  haptic.select();
                }}
                className="text-[11px] text-text-dim hover:text-text-muted transition-colors"
              >
                сбросить
              </button>
            )}
          </div>
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04] max-h-[280px] overflow-y-auto">
            {(templates ?? []).filter((t) => t.kind === "mail").length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-text-muted">
                Нет шаблонов рассылки — будет использован глобальный
              </div>
            ) : (
              (templates ?? [])
                .filter((t) => t.kind === "mail")
                .map((t) => {
                  const active = templateId === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => {
                        setTemplateId(active ? null : t.id);
                        haptic.select();
                      }}
                      className={cn(
                        "w-full text-left px-4 py-3 flex items-center gap-3",
                        "hover:bg-white/[0.03] transition-colors",
                      )}
                    >
                      <div
                        className={cn(
                          "size-5 rounded-md border grid place-items-center shrink-0 transition-colors",
                          active
                            ? "bg-accent border-accent"
                            : "border-white/[0.15] bg-transparent",
                        )}
                      >
                        <AnimatePresence>
                          {active && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              exit={{ scale: 0 }}
                            >
                              <Check className="size-3 text-white" />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium truncate">{t.name}</div>
                        <div className="text-[11px] text-text-muted truncate">
                          {previewText(t.text)}
                        </div>
                      </div>
                    </button>
                  );
                })
            )}
          </div>
        </div>

        {/* Per-chat schedule override */}
        <div className="space-y-3">
          <button
            onClick={() => {
              setCustomScheduleEnabled((v) => !v);
              haptic.select();
            }}
            className={cn(
              "w-full glass rounded-2xl p-3.5 flex items-center gap-3",
              "hover:bg-white/[0.05] transition-colors text-left",
            )}
          >
            <div
              className={cn(
                "size-10 rounded-xl grid place-items-center shrink-0",
                customScheduleEnabled
                  ? "bg-accent-soft text-accent"
                  : "bg-white/[0.04] text-text-dim",
              )}
            >
              <Clock className="size-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">
                {customScheduleEnabled ? "Индивидуальный график" : "Глобальный график"}
              </div>
              <div className="text-xs text-text-muted">
                {customScheduleEnabled
                  ? `${customPostsPerDay} пост./сутки, интервал ${customIntervalMinutes} мин`
                  : "Чат использует общие настройки рассылки"}
              </div>
            </div>
            <div
              className={cn(
                "relative w-10 h-6 rounded-full transition-colors shrink-0",
                customScheduleEnabled ? "bg-accent" : "bg-white/[0.08]",
              )}
            >
              <motion.div
                animate={{ x: customScheduleEnabled ? 16 : 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                className="absolute top-0.5 left-0.5 size-5 rounded-full bg-white"
              />
            </div>
          </button>
          <AnimatePresence>
            {customScheduleEnabled && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    type="number"
                    label="Постов за 24 часа"
                    value={customPostsPerDay}
                    min={0}
                    max={96}
                    onChange={(e) =>
                      setCustomPostsPerDay(clamp(parseInt(e.target.value || "0"), 0, 96))
                    }
                    leftIcon={<CalendarDays />}
                    hint="0 — не публиковать в этот чат"
                  />
                  <Input
                    type="number"
                    label="Интервал, мин"
                    value={customIntervalMinutes}
                    min={1}
                    max={1440}
                    onChange={(e) =>
                      setCustomIntervalMinutes(clamp(parseInt(e.target.value || "0"), 1, 1440))
                    }
                    leftIcon={<Clock />}
                    hint="Минимум между постами"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Button
            variant="ghost"
            className="flex-1"
            leftIcon={<Trash2 />}
            onClick={handleDelete}
            disabled={deleteMut.isPending}
          >
            Удалить
          </Button>
          <Button
            className="flex-1"
            onClick={handleSave}
            disabled={!dirty || updateMut.isPending}
            loading={updateMut.isPending}
            leftIcon={<Power />}
          >
            Сохранить
          </Button>
        </div>
      </div>
    </Sheet>
  );
}

// ============================================================
// Helpers
// ============================================================

function normalizeGuarantor(v: string): string {
  return v.trim().replace(/^@+/, "");
}

function previewText(text: string): string {
  return text
    .replace(/\{([^|}]+)\|[^}]*\}/g, "$1")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
}

function clamp(n: number, min: number, max: number): number {
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function PeerIcon({ type }: { type: Chat["peer_type"] }) {
  if (type === "channel") {
    return <span className="text-xs font-bold">CH</span>;
  }
  if (type === "user") {
    return <span className="text-xs font-bold">U</span>;
  }
  return <span className="text-xs font-bold">GR</span>;
}
